// Pure tile / footprint math extracted 1:1 from RoomCanvas.tsx.
// No PIXI, no React, no component state — plain args in, numbers/arrays out.
// Grid geometry + localToTile live in ./grid (single source); re-exported for consumers.
import {
  FLOOR_WIDTH,
  BASE_MAP_WIDTH_TILES,
  GRID_SUBDIVISION,
  TILE_WIDTH,
} from './grid';
import { isWallMountedFurniture } from '../data/furniture';

export { localToTile, worldToTile, tileToWorld } from './grid';

const BASE_TILE_WIDTH = FLOOR_WIDTH / BASE_MAP_WIDTH_TILES; // 100

export const estimateFootprintTilesFromSpriteWidth = (spriteWidth: number): { width: number; height: number } => {
  // Keep legacy fallback behavior in base grid (100x50), then scale to the
  // finer grid. This preserves previous perceived sizes after subdivision.
  const baseWidth = Math.max(1, Math.round(spriteWidth / BASE_TILE_WIDTH));
  const baseHeight = Math.max(1, Math.round(spriteWidth / (BASE_TILE_WIDTH * 1.2)));
  return {
    width: Math.max(1, baseWidth * GRID_SUBDIVISION),
    height: Math.max(1, baseHeight * GRID_SUBDIVISION),
  };
};

// Rug-specific footprint estimation using isometric diamond geometry.
// A flat rug of W×H tiles forms a diamond with:
//   pixel_width  = (W + H) × TILE_WIDTH / 2
//   pixel_height = (W + H) × TILE_HEIGHT / 2
// The sprite's visualBaseOffsetX encodes the W vs H asymmetry:
//   visualBaseOffsetX = -(W - H) × spriteWidth / (2 × (W + H))
// Solving for W and H individually:
//
// W + H comes from the width only (see below), and W/H are rounded to whole tiles.
export const estimateRugFootprintTiles = (
  spriteWidth: number,
  spriteHeight: number,
  visualBaseOffsetX: number,
): { width: number; height: number } => {
  if (spriteWidth <= 0) return { width: 1, height: 1 };
  // W + H from the sprite WIDTH: a flat iso diamond is (W + H) half-tiles wide.
  // Its height is not used — tassels and the drop shadow make the art taller than
  // the diamond (the big rugs measure 45.8 tile-sums tall vs 40.2 wide), and that
  // inflated rugs by ~3 tiles: every rug near a wall counted as "in the wall" (red).
  void spriteHeight;
  const maxSum = (2 * spriteWidth) / TILE_WIDTH;
  // Derive W - H from the visual offset
  const diff = -2 * maxSum * visualBaseOffsetX / spriteWidth;
  // Use round for a tighter footprint that matches visible opaque content.
  // ceil over-estimates by 1 tile, causing a gap when the rug is pushed
  // against the wall (the sprite content doesn't fill that extra tile).
  const W = Math.max(1, Math.round((maxSum + diff) / 2));
  const H = Math.max(1, Math.round((maxSum - diff) / 2));
  return { width: W, height: H };
};

// Mirror-aware footprint tile offset: isometric horizontal flip swaps the X and Y iso axes,
// so mirrored items swap dx↔dy when mapping footprint_tiles to world tile coords.
export const mirrorFpOffset = (dx: number, dy: number, mirrored: boolean): [number, number] =>
  mirrored ? [dy, dx] : [dx, dy];

// Rugs always use frontend sprite-based footprint estimation (never trust backend tiles).
export const isRugCategory = (category?: string | null): boolean => category === 'rugs';

// Check if furniture is wall-mounted (windows or paintings/wall decorations)
// Wall-mounted objects should be placed on walls like windows
export const isWallMountedItem = (catalog?: { category?: string } | null): boolean => {
  if (!catalog || !catalog.category) return false;
  // Check if category is in wall-mounted categories
  return isWallMountedFurniture(catalog);
};

// Window footprint: the widest opaque row of the sprite determines how many
// tiles the window spans along the wall.  Thickness is always 1.
export const estimateWindowFootprintTiles = (
  maxOpaqueWidthPx: number,
): { width: number; height: number } => {
  if (maxOpaqueWidthPx <= 0) return { width: 2, height: 1 };
  const halfTileWidth = TILE_WIDTH / 2;
  const width = Math.max(2, Math.round(maxOpaqueWidthPx / halfTileWidth));
  return { width, height: 1 };
};

// Returns tile dimensions for a furniture item. For rugs and windows/wall-mounted, forces
// null so getFurnitureDepthComponents will use sprite-based estimation instead.
// When footprint_tiles is set, the bounding box of those tiles is used for w/h.
// Otherwise returns null so getFurnitureDepthComponents estimates from sprite size.
export const getTileDims = (catalog?: { category?: string; id?: number; footprint_tiles?: Array<Array<number>> | null } | null): { w: number | null; h: number | null; isRug: boolean; isWindow: boolean } => {
  const rug = !!catalog && isRugCategory(catalog.category);
  const win = !!catalog && isWallMountedItem(catalog);
  if (!catalog || rug || win) return { w: null, h: null, isRug: rug, isWindow: win };
  // Admin-defined footprint_tiles: derive bounding box from them.
  if (catalog.footprint_tiles && catalog.footprint_tiles.length > 0) {
    const dxs = catalog.footprint_tiles.map(([dx]) => dx);
    const dys = catalog.footprint_tiles.map(([, dy]) => dy);
    const w = Math.max(...dxs) - Math.min(...dxs) + 1;
    const h = Math.max(...dys) - Math.min(...dys) + 1;
    return { w, h, isRug: false, isWindow: false };
  }
  // No footprint defined: frontend will estimate from sprite dimensions.
  return { w: null, h: null, isRug: false, isWindow: false };
};

// ── Window / wall placement helpers ──
// In our isometric tile grid the two walls are:
//   Left  wall → tiles with tileX = 0  (the edge running from top vertex to left vertex)
//   Right wall → tiles with tileY = 0  (the edge running from top vertex to right vertex)
// A window is valid when its footprint's back edge is flush against one of these walls.
// Returns which wall the footprint touches, or null if it's not on any wall.
export const getWindowWallSide = (baseTileX: number, baseTileY: number): 'left' | 'right' | null => {
  if (baseTileX === 0) return 'left';
  if (baseTileY === 0) return 'right';
  return null;
};

